/**
 * Hook for managing address fields
 */
import { useMemo, useState, useCallback, useEffect } from "react";
import { useSignup as useSignupContext } from "../../SignupContext";
import { debounce } from "../../utils/debounce";
import { validateStreet, validateBuildingNumber, validateInteriorNumber, validateDistrict, validateCity, validateState, validatePostalCode, validatePlaceId, } from "../../validation/address";
export function useAddress() {
    const { formValues, setFormValues, apiClient } = useSignupContext();
    // State for address autocomplete suggestions
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = useState(false);
    // Generate a unique session ID for Google Places API in proper UUID format
    const [placesSessionId] = useState(() => {
        // Generate UUID in proper format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
        // where x is any hexadecimal digit and y is one of 8, 9, A, or B
        const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
        // Ensure the UUID has the correct format with hyphens
        // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid)) {
            // Fallback to a more explicit UUID generation if the pattern replacement fails
            const segments = [
                Math.random().toString(16).substring(2, 10),
                Math.random().toString(16).substring(2, 6),
                "4" + Math.random().toString(16).substring(2, 5),
                ((8 + Math.random() * 4) | 0).toString(16) +
                    Math.random().toString(16).substring(2, 5),
                Math.random().toString(16).substring(2, 14),
            ];
            return segments.join("-");
        }
        return uuid;
    });
    // Touch and blur states for each field
    const [streetTouch, setStreetTouch] = useState({
        touched: false,
        blurred: false,
    });
    const [buildingNumberTouch, setBuildingNumberTouch] = useState({
        touched: false,
        blurred: false,
    });
    const [interiorNumberTouch, setInteriorNumberTouch] = useState({
        touched: false,
        blurred: false,
    });
    const [colonyTouch, setColonyTouch] = useState({
        touched: false,
        blurred: false,
    });
    const [cityTouch, setCityTouch] = useState({
        touched: false,
        blurred: false,
    });
    const [stateTouch, setStateTouch] = useState({
        touched: false,
        blurred: false,
    });
    const [postalCodeTouch, setPostalCodeTouch] = useState({
        touched: false,
        blurred: false,
    });
    // Validation for all fields
    const validation = useMemo(() => {
        const streetValidation = validateStreet(formValues.address.street);
        const buildingNumberValidation = validateBuildingNumber(formValues.address.buildingNumber);
        const interiorNumberValidation = validateInteriorNumber(formValues.address.interiorNumber);
        const colonyValidation = validateDistrict(formValues.address.colony);
        const cityValidation = validateCity(formValues.address.city);
        const stateValidation = validateState(formValues.address.state);
        const postalCodeValidation = validatePostalCode(formValues.address.postalCode);
        // Use the standardized validation function for placeId
        const placeIdValidation = validatePlaceId(formValues.address.placeId);
        return {
            street: streetValidation,
            buildingNumber: buildingNumberValidation,
            interiorNumber: interiorNumberValidation,
            colony: colonyValidation,
            city: cityValidation,
            state: stateValidation,
            postalCode: postalCodeValidation,
            placeId: placeIdValidation,
            isValid: streetValidation.isValid &&
                buildingNumberValidation.isValid &&
                interiorNumberValidation.isValid &&
                colonyValidation.isValid &&
                cityValidation.isValid &&
                stateValidation.isValid &&
                postalCodeValidation.isValid &&
                placeIdValidation.isValid,
        };
    }, [formValues.address, formValues.language]); // Include language for reactivity
    // Field setter functions
    const setStreet = (value) => {
        setFormValues.setAddress({
            ...formValues.address,
            street: value,
        });
    };
    const setBuildingNumber = (value) => {
        setFormValues.setAddress({
            ...formValues.address,
            buildingNumber: value,
        });
    };
    const setInteriorNumber = (value) => {
        setFormValues.setAddress({
            ...formValues.address,
            interiorNumber: value,
        });
    };
    const setColony = (value) => {
        setFormValues.setAddress({
            ...formValues.address,
            colony: value,
        });
    };
    const setCity = (value) => {
        setFormValues.setAddress({
            ...formValues.address,
            city: value,
        });
    };
    const setState = (value) => {
        setFormValues.setAddress({
            ...formValues.address,
            state: value,
        });
    };
    const setPostalCode = (value) => {
        setFormValues.setAddress({
            ...formValues.address,
            postalCode: value,
        });
    };
    const setPlaceId = (value) => {
        setFormValues.setAddress({
            ...formValues.address,
            placeId: value,
        });
    };
    // Function to fetch address suggestions based on user input
    const fetchAddressSuggestions = useCallback(async (input) => {
        if (!input || input.length < 3) {
            setAddressSuggestions([]);
            return;
        }
        setIsLoadingAddressSuggestions(true);
        try {
            const response = await apiClient.autocompleteAddressWithData(input, placesSessionId);
            setAddressSuggestions(response.addresses);
        }
        catch (error) {
            console.error("Error fetching address suggestions:", error);
            setAddressSuggestions([]);
        }
        finally {
            setIsLoadingAddressSuggestions(false);
        }
    }, [apiClient, placesSessionId]);
    // Debounced version of fetchAddressSuggestions to avoid excessive API calls
    const debouncedFetchSuggestions = useMemo(() => debounce(fetchAddressSuggestions, 300), [fetchAddressSuggestions]);
    // Reset suggestions
    const resetSuggestions = useCallback(() => {
        setAddressSuggestions([]);
    }, []);
    // Watch for changes to street field and fetch suggestions
    useEffect(() => {
        if (formValues.address.street && formValues.address.street.length >= 3) {
            debouncedFetchSuggestions(formValues.address.street);
        }
        else {
            setAddressSuggestions([]);
        }
    }, [formValues.address.street, debouncedFetchSuggestions]);
    // Handle selection of an address suggestion
    const handleAddressSuggestionSelect = useCallback(async (suggestion) => {
        setPlaceId(suggestion.placeId);
        setStreet(suggestion.mainText);
        // Fetch full address details based on place ID
        try {
            const addressDetails = await apiClient.getAddressWithData(suggestion.placeId, placesSessionId);
            // Update address fields with the fetched details
            if (addressDetails.addressMX) {
                const addressMX = addressDetails.addressMX;
                setFormValues.setAddress({
                    ...formValues.address,
                    street: addressMX.street || formValues.address.street,
                    buildingNumber: addressMX.buildingNumber || formValues.address.buildingNumber,
                    interiorNumber: addressMX.interiorNumber || formValues.address.interiorNumber,
                    colony: addressMX.colony || formValues.address.colony,
                    city: addressMX.city || formValues.address.city,
                    state: addressMX.state || formValues.address.state,
                    postalCode: addressMX.postalCode || formValues.address.postalCode,
                    placeId: suggestion.placeId,
                });
            }
        }
        catch (error) {
            console.error("Error fetching address details:", error);
        }
        // Clear suggestions after selection
        resetSuggestions();
    }, [
        apiClient,
        formValues.address,
        resetSuggestions,
        setFormValues,
        setPlaceId,
        setStreet,
        placesSessionId,
    ]);
    return useMemo(() => ({
        street: {
            value: formValues.address.street,
            touched: streetTouch.touched,
            blurred: streetTouch.blurred,
            isValid: validation.street.isValid,
            errorMessage: validation.street.errorMessage,
            setValue: setStreet,
            setTouched: (touched) => setStreetTouch((prev) => ({ ...prev, touched })),
            setBlurred: (blurred) => setStreetTouch((prev) => ({ ...prev, blurred })),
        },
        buildingNumber: {
            value: formValues.address.buildingNumber,
            touched: buildingNumberTouch.touched,
            blurred: buildingNumberTouch.blurred,
            isValid: validation.buildingNumber.isValid,
            errorMessage: validation.buildingNumber.errorMessage,
            setValue: setBuildingNumber,
            setTouched: (touched) => setBuildingNumberTouch((prev) => ({ ...prev, touched })),
            setBlurred: (blurred) => setBuildingNumberTouch((prev) => ({ ...prev, blurred })),
        },
        interiorNumber: {
            value: formValues.address.interiorNumber,
            touched: interiorNumberTouch.touched,
            blurred: interiorNumberTouch.blurred,
            isValid: validation.interiorNumber.isValid,
            errorMessage: validation.interiorNumber.errorMessage,
            setValue: setInteriorNumber,
            setTouched: (touched) => setInteriorNumberTouch((prev) => ({ ...prev, touched })),
            setBlurred: (blurred) => setInteriorNumberTouch((prev) => ({ ...prev, blurred })),
        },
        colony: {
            value: formValues.address.colony,
            touched: colonyTouch.touched,
            blurred: colonyTouch.blurred,
            isValid: validation.colony.isValid,
            errorMessage: validation.colony.errorMessage,
            setValue: setColony,
            setTouched: (touched) => setColonyTouch((prev) => ({ ...prev, touched })),
            setBlurred: (blurred) => setColonyTouch((prev) => ({ ...prev, blurred })),
        },
        city: {
            value: formValues.address.city,
            touched: cityTouch.touched,
            blurred: cityTouch.blurred,
            isValid: validation.city.isValid,
            errorMessage: validation.city.errorMessage,
            setValue: setCity,
            setTouched: (touched) => setCityTouch((prev) => ({ ...prev, touched })),
            setBlurred: (blurred) => setCityTouch((prev) => ({ ...prev, blurred })),
        },
        state: {
            value: formValues.address.state,
            touched: stateTouch.touched,
            blurred: stateTouch.blurred,
            isValid: validation.state.isValid,
            errorMessage: validation.state.errorMessage,
            setValue: setState,
            setTouched: (touched) => setStateTouch((prev) => ({ ...prev, touched })),
            setBlurred: (blurred) => setStateTouch((prev) => ({ ...prev, blurred })),
        },
        postalCode: {
            value: formValues.address.postalCode,
            touched: postalCodeTouch.touched,
            blurred: postalCodeTouch.blurred,
            isValid: validation.postalCode.isValid,
            errorMessage: validation.postalCode.errorMessage,
            setValue: setPostalCode,
            setTouched: (touched) => setPostalCodeTouch((prev) => ({ ...prev, touched })),
            setBlurred: (blurred) => setPostalCodeTouch((prev) => ({ ...prev, blurred })),
        },
        placeId: {
            value: formValues.address.placeId,
            setValue: setPlaceId,
        },
        autocompleteSuggestions: {
            isLoading: isLoadingAddressSuggestions,
            suggestions: addressSuggestions,
            handleAddressSuggestionSelect,
            resetSuggestions,
        },
        isValid: validation.isValid,
        setAddress: setFormValues.setAddress,
    }), [
        formValues.address,
        validation,
        streetTouch,
        buildingNumberTouch,
        interiorNumberTouch,
        colonyTouch,
        cityTouch,
        stateTouch,
        postalCodeTouch,
        addressSuggestions,
        isLoadingAddressSuggestions,
        handleAddressSuggestionSelect,
        resetSuggestions,
        setFormValues.setAddress,
    ]);
}
